import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import type { Role } from '@fkwd-qc/shared';
import { useSession } from '@/hooks/use-session';
import { EmptyState } from './empty-state';
import { LoadingRows } from './loading-rows';

/**
 * Props for the route guard.
 *
 * @since 1.0.0
 */
export interface ProtectedRouteProps {
    minRole?: Role;
    children?: ReactNode;
}

/**
 * Sends anonymous visitors to the sign-in page and refuses views above the signed-in role.
 * Used both as a layout route and as a wrapper around a single view.
 *
 * @since 1.0.0
 * @param props - the minimum role required and an optional subtree to guard instead of the outlet
 * @returns the guarded subtree, a redirect, or a refusal
 */
export function ProtectedRoute({ minRole, children }: ProtectedRouteProps) {
    const { isAuthenticated, isLoading, can } = useSession();
    const location = useLocation();

    if (isLoading) {
        return <LoadingRows label="Checking your session" />;
    }

    if (!isAuthenticated) {
        return <Navigate replace state={{ from: `${location.pathname}${location.search}` }} to="/login" />;
    }

    if (minRole !== undefined && !can(minRole)) {
        return (
            <div className="fkwd-card">
                <EmptyState title="You do not have access to this view" detail="Ask an admin to raise your role." />
            </div>
        );
    }

    return <>{children ?? <Outlet />}</>;
}
